import { useEffect, useState, useCallback } from 'react';
import { listFormStatesFromS3, loadFormStateFromS3, deleteFormStateFromS3, formatAEST } from '../services/formStateS3Service';

const LoadFormModal = ({ isOpen, onClose, onSelectForm, username }) => {
      const [forms, setForms] = useState([]);
      const [loading, setLoading] = useState(false);
      const [error, setError] = useState(null);
      const [sortConfig, setSortConfig] = useState({ key: 'savedAt', direction: 'desc' });

      const loadForms = useCallback(async () => {
            setLoading(true);
            setError(null);
            try {
                  const result = await listFormStatesFromS3(username, true); // Force refresh
                  setForms(result.forms || []);
            } catch (err) {
                  console.error('Error loading forms:', err);
                  setError(err.message || 'Failed to load saved forms');
            } finally {
                  setLoading(false);
            }
      }, [username]);

      useEffect(() => {
            if (isOpen && username) {
                  loadForms();
                  document.body.style.overflow = 'hidden';
            } else {
                  document.body.style.overflow = 'unset';
            }
            return () => {
                  document.body.style.overflow = 'unset';
            };
      }, [isOpen, username, loadForms]);

      const handleLoadForm = async (referenceNo) => {
            try {
                  setLoading(true);
                  const result = await loadFormStateFromS3(username, referenceNo);
                  if (result.formState) {
                        onSelectForm(result.formState);
                        onClose();
                  }
            } catch (err) {
                  console.error('Error loading form:', err);
                  setError(err.message || 'Failed to load form');
            } finally {
                  setLoading(false);
            }
      };

      const handleDeleteForm = async (referenceNo, e) => {
            e.stopPropagation(); // Prevent row click
            if (!window.confirm(`Delete saved form ${referenceNo}?`)) {
                  return;
            }
            try {
                  await deleteFormStateFromS3(username, referenceNo);
                  // Reload forms list
                  await loadForms();
            } catch (err) {
                  console.error('Error deleting form:', err);
                  setError(err.message || 'Failed to delete form');
            }
      };

      const handleSort = (key) => {
            let direction = 'asc';
            if (sortConfig.key === key && sortConfig.direction === 'asc') {
                  direction = 'desc';
            }
            setSortConfig({ key, direction });
      };

      const sortedForms = [...forms].sort((a, b) => {
            const aValue = a[sortConfig.key];
            const bValue = b[sortConfig.key];
            
            if (aValue < bValue) {
                  return sortConfig.direction === 'asc' ? -1 : 1;
            }
            if (aValue > bValue) {
                  return sortConfig.direction === 'asc' ? 1 : -1;
            }
            return 0;
      });

      if (!isOpen) return null;

      return (
            <div style={{
                  position: 'fixed',
                  top: 0,
                  left: 0,
                  right: 0,
                  bottom: 0,
                  backgroundColor: 'rgba(0,0,0,0.5)',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  zIndex: 9999,
                  padding: '20px'
            }}>
                  <div style={{
                        backgroundColor: 'white',
                        borderRadius: '8px',
                        maxWidth: '900px',
                        width: '100%',
                        maxHeight: '80vh',
                        display: 'flex',
                        flexDirection: 'column',
                        boxShadow: '0 4px 6px rgba(0,0,0,0.1)'
                  }}>
                        {/* Header */}
                        <div style={{
                              padding: '20px',
                              borderBottom: '1px solid #e5e7eb',
                              display: 'flex',
                              justifyContent: 'space-between',
                              alignItems: 'center'
                        }}>
                              <h2 style={{ margin: 0, fontSize: '1.5rem', fontWeight: 'bold' }}>
                                    Load Saved Form
                              </h2>
                              <button
                                    onClick={onClose}
                                    style={{
                                          background: 'none',
                                          border: 'none',
                                          fontSize: '1.5rem',
                                          cursor: 'pointer',
                                          color: '#6b7280'
                                    }}
                              >
                                    ×
                              </button>
                        </div>

                        {/* Content */}
                        <div style={{
                              padding: '20px',
                              overflowY: 'auto',
                              flex: 1
                        }}>
                              {error && (
                                    <div style={{
                                          padding: '12px',
                                          marginBottom: '16px',
                                          backgroundColor: '#fee2e2',
                                          color: '#991b1b',
                                          borderRadius: '4px',
                                          display: 'flex',
                                          justifyContent: 'space-between',
                                          alignItems: 'center'
                                    }}>
                                          <span>{error}</span>
                                          <button
                                                onClick={() => setError(null)}
                                                style={{
                                                      background: 'none',
                                                      border: 'none',
                                                      color: '#991b1b',
                                                      cursor: 'pointer',
                                                      fontSize: '1.2rem'
                                                }}
                                          >
                                                ×
                                          </button>
                                    </div>
                              )}

                              {loading && (
                                    <div style={{ textAlign: 'center', padding: '40px' }}>
                                          <div style={{
                                                border: '4px solid #f3f4f6',
                                                borderTop: '4px solid #3b82f6',
                                                borderRadius: '50%',
                                                width: '40px',
                                                height: '40px',
                                                animation: 'spin 1s linear infinite',
                                                margin: '0 auto'
                                          }} />
                                          <p style={{ marginTop: '16px', color: '#6b7280' }}>
                                                Loading forms...
                                          </p>
                                    </div>
                              )}

                              {!loading && forms.length === 0 && (
                                    <div style={{
                                          textAlign: 'center',
                                          padding: '40px',
                                          color: '#6b7280'
                                    }}>
                                          <p style={{ fontSize: '1.1rem' }}>No saved forms found</p>
                                          <p style={{ fontSize: '0.9rem', marginTop: '8px' }}>
                                                Save a form to see it here
                                          </p>
                                    </div>
                              )}

                              {!loading && forms.length > 0 && (
                                    <div style={{ overflowX: 'auto' }}>
                                          <table style={{
                                                width: '100%',
                                                borderCollapse: 'collapse',
                                                fontSize: '0.95rem'
                                          }}>
                                                <thead>
                                                      <tr style={{ backgroundColor: '#f9fafb', borderBottom: '2px solid #e5e7eb' }}>
                                                            <th
                                                                  onClick={() => handleSort('savedAt')}
                                                                  style={{
                                                                        padding: '12px',
                                                                        textAlign: 'left',
                                                                        fontWeight: '600',
                                                                        cursor: 'pointer',
                                                                        userSelect: 'none'
                                                                  }}
                                                            >
                                                                  Saved (AEST) {sortConfig.key === 'savedAt' && (sortConfig.direction === 'asc' ? '↑' : '↓')}
                                                            </th>
                                                            <th
                                                                  onClick={() => handleSort('referenceNo')}
                                                                  style={{
                                                                        padding: '12px',
                                                                        textAlign: 'left',
                                                                        fontWeight: '600',
                                                                        cursor: 'pointer',
                                                                        userSelect: 'none'
                                                                  }}
                                                            >
                                                                  Reference Number {sortConfig.key === 'referenceNo' && (sortConfig.direction === 'asc' ? '↑' : '↓')}
                                                            </th>
                                                            <th
                                                                  onClick={() => handleSort('eventName')}
                                                                  style={{
                                                                        padding: '12px',
                                                                        textAlign: 'left',
                                                                        fontWeight: '600',
                                                                        cursor: 'pointer',
                                                                        userSelect: 'none'
                                                                  }}
                                                            >
                                                                  Event Name {sortConfig.key === 'eventName' && (sortConfig.direction === 'asc' ? '↑' : '↓')}
                                                            </th>
                                                            <th
                                                                  onClick={() => handleSort('sectionName')}
                                                                  style={{
                                                                        padding: '12px',
                                                                        textAlign: 'left',
                                                                        fontWeight: '600',
                                                                        cursor: 'pointer',
                                                                        userSelect: 'none'
                                                                  }}
                                                            >
                                                                  Section {sortConfig.key === 'sectionName' && (sortConfig.direction === 'asc' ? '↑' : '↓')}
                                                            </th>
                                                            <th style={{
                                                                  padding: '12px',
                                                                  textAlign: 'center',
                                                                  fontWeight: '600',
                                                                  width: '80px'
                                                            }}>
                                                                  Actions
                                                            </th>
                                                      </tr>
                                                </thead>
                                                <tbody>
                                                      {sortedForms.map((form, index) => (
                                                            <tr
                                                                  key={form.referenceNo}
                                                                  onClick={() => handleLoadForm(form.referenceNo)}
                                                                  style={{
                                                                        backgroundColor: index % 2 === 0 ? 'white' : '#f9fafb',
                                                                        borderBottom: '1px solid #e5e7eb',
                                                                        cursor: 'pointer',
                                                                        transition: 'background-color 0.15s'
                                                                  }}
                                                                  onMouseEnter={(e) => {
                                                                        e.currentTarget.style.backgroundColor = '#eff6ff';
                                                                  }}
                                                                  onMouseLeave={(e) => {
                                                                        e.currentTarget.style.backgroundColor = index % 2 === 0 ? 'white' : '#f9fafb';
                                                                  }}
                                                            >
                                                                  <td style={{ padding: '12px', color: '#6b7280' }}>
                                                                        {formatAEST(form.savedAt)}
                                                                  </td>
                                                                  <td style={{ padding: '12px', fontWeight: '500' }}>
                                                                        {form.referenceNo}
                                                                  </td>
                                                                  <td style={{ padding: '12px' }}>
                                                                        {form.eventName}
                                                                  </td>
                                                                  <td style={{ padding: '12px' }}>
                                                                        {form.sectionName || (form.formState && form.formState.sectionName) || ''}
                                                                  </td>
                                                                  <td style={{ padding: '12px', textAlign: 'center' }}>
                                                                        <button
                                                                              onClick={(e) => handleDeleteForm(form.referenceNo, e)}
                                                                              style={{
                                                                                    background: 'none',
                                                                                    border: '1px solid #ef4444',
                                                                                    color: '#ef4444',
                                                                                    padding: '4px 12px',
                                                                                    borderRadius: '4px',
                                                                                    cursor: 'pointer',
                                                                                    fontSize: '0.85rem',
                                                                                    transition: 'all 0.15s'
                                                                              }}
                                                                              onMouseEnter={(e) => {
                                                                                    e.currentTarget.style.backgroundColor = '#ef4444';
                                                                                    e.currentTarget.style.color = 'white';
                                                                              }}
                                                                              onMouseLeave={(e) => {
                                                                                    e.currentTarget.style.backgroundColor = 'transparent';
                                                                                    e.currentTarget.style.color = '#ef4444';
                                                                              }}
                                                                        >
                                                                              Delete
                                                                        </button>
                                                                  </td>
                                                            </tr>
                                                      ))}
                                                </tbody>
                                          </table>
                                    </div>
                              )}
                        </div>

                        {/* Footer */}
                        <div style={{
                              padding: '16px 20px',
                              borderTop: '1px solid #e5e7eb',
                              display: 'flex',
                              justifyContent: 'space-between',
                              alignItems: 'center',
                              backgroundColor: '#f9fafb'
                        }}>
                              <span style={{ fontSize: '0.9rem', color: '#6b7280' }}>
                                    {forms.length} form{forms.length !== 1 ? 's' : ''} found
                              </span>
                              <button
                                    onClick={onClose}
                                    style={{
                                          padding: '8px 16px',
                                          backgroundColor: '#6b7280',
                                          color: 'white',
                                          border: 'none',
                                          borderRadius: '4px',
                                          cursor: 'pointer',
                                          fontSize: '0.95rem'
                                    }}
                              >
                                    Close
                              </button>
                        </div>
                  </div>

                  {/* CSS Animation */}
                  <style>
                        {`
                              @keyframes spin {
                                    0% { transform: rotate(0deg); }
                                    100% { transform: rotate(360deg); }
                              }
                        `}
                  </style>
            </div>
      );
};

export default LoadFormModal;
